import { Box, Container, Typography, useMediaQuery, useTheme } from '@mui/material';
import Image from 'next/image';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import BottomNav from '@/components/BottomNav';
import DonationForm from '@/components/Donations/DonationForm';
import { LoadingSpinner } from '@/components/Loading';
import { useAuth } from '@/contexts/AuthContext';
import { useEvent } from '@/contexts/EventsContext';

export default function Donations() {
  const { t, i18n } = useTranslation();
  const [isLoading, setIsLoading] = useState(true);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { active, areDonationsOpen } = useEvent();
  const router = useRouter();
  const { user } = useAuth();

  useEffect(() => {
    if (active && !areDonationsOpen && !user?.isStaff) router.push('/');
  }, [active, areDonationsOpen, router]);

  if (isLoading) return <LoadingSpinner setIsLoading={setIsLoading} />;

  if (!active) {
    return (
      <Container maxWidth="xl">
        <Typography gutterBottom variant="h2" className="font-bebas">
          {t('event.notlive')}
        </Typography>
        {isMobile && <BottomNav />}
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ paddingBottom: isMobile ? 12 : 4 }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', width: '100%' }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            justifyContent: 'space-between',
            alignItems: { xs: 'center', md: 'middle' },
            width: '100%',
            marginTop: 2,
            marginBottom: 0,
          }}
        >
          <Typography
            gutterBottom
            variant="h2"
            component="h1"
            className="font-bebas"
            sx={{ marginBottom: 0 }}
            textAlign={isMobile ? 'center' : 'left'}
          >
            {active.name}
          </Typography>

          <Box
            sx={[
              {
                display: 'flex',
                flexDirection: 'column',
              },
              isMobile
                ? {
                    alignItems: 'center',
                    textAlign: 'center',
                  }
                : {
                    alignItems: 'flex-end',
                    textAlign: 'right',
                  },
            ]}
          >
            <Image
              src={`/imgs/donations/logo_gardiens_${i18n.language}.png`}
              alt={t('charity.name')}
              width={200}
              height={200}
              style={{ margin: isMobile ? '0.5em 0' : '0' }}
            />
            <Typography variant="body1">{t('donation.supportingGardiens')}</Typography>
          </Box>
        </Box>

        {/* If donations are not opened but user is staff, we allow it but notifies them */}
        {!areDonationsOpen && user && user.isStaff && (
          <Typography
            variant="h6"
            color="error"
            sx={{ width: '100%', marginTop: 2, fontSize: '0.8em', textAlign: 'center' }}
          >
            Donations are curently disabled, but as a staff member you have access to this page.
            <br />
            {!active.allowDonations && ' Donations are disabled for this event.'}
            <br />
            {active.donationStartAt
              && ` Donations are configured to start at ${new Date(active.donationStartAt).toLocaleString()}.`}
            <br />
            {active.donationEndAt
              && ` Donations are configured to end at ${new Date(active.donationEndAt).toLocaleString()}.`}
          </Typography>
        )}

        <DonationForm eventId={active.id} />
      </Box>
      {isMobile && <BottomNav />}
    </Container>
  );
}
